import { useEffect, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '../../lib/cn';
import { useDebounced } from '../../lib/useDebounced';
import { Input } from '../../components/ui/Field';

/** How long typing has to pause before the list is searched. */
export const SEARCH_DEBOUNCE_MS = 300;

/** The server's limit on a search (`MaxLength(100)` on the page routes). */
const SEARCH_MAX_LENGTH = 100;

/**
 * The search box over a paged list. Typing searches once it pauses - searching on every key would
 * fetch a page per letter - and the clear button empties it at once. `onSearch` receives the text
 * trimmed; '' means no search.
 */
export function MediaSearchBox({
  value,
  onSearch,
  label = 'Search media',
  className,
}: {
  /** The search in force: what `onSearch` was last given. */
  value: string;
  onSearch: (text: string) => void;
  /** Placeholder and accessible name, e.g. "Search items" over a source's items. */
  label?: string;
  className?: string;
}) {
  const [text, setText] = useState(value);
  /** How many times the clear button was pressed. */
  const [clears, setClears] = useState(0);
  // A clear empties `settled` at once as well. Left to the pause it would go on holding the old
  // word, and the same word typed again before the pause was over would never count as a change.
  const settled = useDebounced(text, SEARCH_DEBOUNCE_MS, clears);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const next = settled.trim();
    if (next !== value) onSearch(next);
    // Only the settled TEXT searches: `value` catching up with it must not send it again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settled]);

  const clear = () => {
    setText('');
    setClears((n) => n + 1);
    if (value !== '') onSearch('');
    // The button goes away with the text; keep the keyboard in the box rather than on the page.
    input.current?.focus();
  };

  return (
    <div className={cn('relative', className)}>
      <Search
        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-faint"
        aria-hidden
      />
      <Input
        ref={input}
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={label}
        aria-label={label}
        maxLength={SEARCH_MAX_LENGTH}
        className="pl-9 pr-9"
      />
      {text !== '' && (
        <button
          type="button"
          onClick={clear}
          aria-label="Clear search"
          className="focus-ring absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-ink-faint hover:text-ink-label"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      )}
    </div>
  );
}
